/* eslint-disable no-storage/no-browser-storage */
import type {
  EnsureSerializable,
  Serializable,
  SerializableObject,
} from '../types/utils';

/** the types of storage available */
export type StorageType = 'local' | 'session';

/**
 * typed abstraction for interacting with storage
 * @see docs: https://components.frame.io/?path=/story/utils-storage-utils-docs--page
 */
export class Storage<S extends SerializableObject> {
  /**
   * serialize a value for storage
   * @param value the value to serialize
   */
  static serialize = <T extends Serializable>(value: T): string =>
    JSON.stringify(value);

  /**
   * deserialize a value for storage
   * @param value the value to deserialize
   */
  static deserialize = <T extends Serializable>(value: string): T =>
    JSON.parse(value);

  /**
   * set a key in storage to a value
   * @param key the key of the storage object to set
   * @param value the value to set at key
   * @param storageType what type of storage to use
   */
  set<K extends keyof S>(
    key: K,
    value: Infer<S[K]>,
    storageType?: StorageType
  ): void;

  /**
   * set a key in storage to a value using a setter function
   * @param key the key of the storage object to set
   * @param value a setter function that takes in the previous value and returns the new value
   * @param storageType what type of storage to use
   */
  set<K extends keyof S>(
    key: K,
    value: SetterFn<S[K]>,
    storageType?: StorageType
  ): void;

  set<K extends keyof S>(
    key: K,
    value: SetterFn<S[K]> & Infer<S[K]>,
    storageType?: StorageType
  ): void {
    let val = value as S[K];
    if (typeof value === 'function') {
      const prevVal = this.get(key, undefined, storageType);
      val = value(prevVal as S[K]);
    }
    const serializedValue = Storage.serialize(val);
    return this.doStorageOperation(
      'setItem',
      storageType,
      String(key),
      serializedValue
    );
  }

  /**
   * @param key the key of the storage object to get
   * @param storageType what type of storage to use
   * @param initialValue if the key is not found, initialize the value to this partial shape
   */
  get = <K extends keyof S>(
    key: K,
    initialValue?: S[K],
    storageType: StorageType = 'local'
  ): S[K] | undefined => {
    let value: S[K] | undefined;
    try {
      value = Storage.deserialize<S[K]>(
        this.doStorageOperation('getItem', storageType, String(key))
      );
    } catch {
      // storage not available
    }

    /** if the value doesnt exist (null) or storage is not avail (undefined) set the initialValue */
    if ((value === null || value === undefined) && initialValue !== undefined) {
      this.set(key, initialValue as Infer<S[K]>, storageType);
      value = initialValue;
    }
    return value;
  };

  /**
   * delete the storage value for a given key
   * @param key the key of the storage object to delete
   * @param storageType what type of storage to access
   */
  remove = <K extends keyof S>(
    key: K,
    storageType: StorageType = 'local'
  ): void => {
    this.doStorageOperation('removeItem', storageType, String(key));
  };

  /**
   * clear all storage values
   * @param storageType what type of storage to access
   */
  clear = (storageType: StorageType = 'local'): void => {
    this.doStorageOperation('clear', storageType);
  };

  /** perform an operation on globalThis.Storage */
  private doStorageOperation = (
    operation: keyof globalThis.Storage,
    storageType: StorageType = 'local',
    ...args: unknown[]
  ) => {
    try {
      let storage: globalThis.Storage | undefined;
      if (storageType === 'local') storage = globalThis?.localStorage;
      if (storageType === 'session') storage = globalThis?.sessionStorage;
      return storage?.[operation](...args) ?? undefined;
    } catch {
      // storage not available
    }
    return undefined;
  };
}

/** a setter function that takes in the previous storage value and returns the new value */
type SetterFn<T> = (previous: T) => T;

/** infers the base ensured serializable type */
type Infer<T> = T extends EnsureSerializable<infer U> ? U : T;
